import React from "react";
import {
  Box,
  Button,
  Checkbox,
  HStack,
  IconButton,
  Input,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { useFieldArray, useWatch } from "react-hook-form";
import { BsArrowDown, BsArrowUp, BsFiles, BsPlus, BsTrash } from "react-icons/bs";
import { cloneTemplateItem, createTemplateItem } from "./templateSectionUtils";

const KraItemsTable = ({ control, sectionIndex, register, setValue }) => {
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const columnHeaderBg = useColorModeValue("blue.50", "gray.700");
  const mobileRowBg = useColorModeValue("white", "gray.800");
  const labelColor = useColorModeValue("gray.600", "gray.400");
  const { fields, append, remove, move, insert } = useFieldArray({
    control,
    name: `sections.${sectionIndex}.items`,
    keyName: "fieldKey",
  });
  const section = useWatch({ control, name: `sections.${sectionIndex}` }) || {};
  const items = section.items || [];

  const addRow = () => append(createTemplateItem({ sequence: fields.length + 1 }));
  const duplicateRow = (index) =>
    insert(index + 1, cloneTemplateItem(items[index], { sequence: index + 2 }));

  React.useEffect(() => {
    fields.forEach((_, index) => {
      if (items[index]?.sequence !== index + 1) {
        setValue(`sections.${sectionIndex}.items.${index}.sequence`, index + 1, { shouldDirty: true });
      }
    });
  }, [fields, items, sectionIndex, setValue]);

  return (
    <>
      <VStack display={{ base: "flex", md: "none" }} align="stretch" spacing={3}>
        {fields.map((field, index) => (
          <Box
            key={field.fieldKey}
            bg={mobileRowBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
            p={3}
          >
            <HStack justify="space-between" align="center" mb={3}>
              <Text fontSize="sm" fontWeight="semibold">KRA row {index + 1}</Text>
              <HStack spacing={1}>
                <IconButton size="xs" variant="ghost" icon={<BsArrowUp />} aria-label="Move KRA row up" onClick={() => index > 0 && move(index, index - 1)} />
                <IconButton size="xs" variant="ghost" icon={<BsArrowDown />} aria-label="Move KRA row down" onClick={() => index < fields.length - 1 && move(index, index + 1)} />
                <IconButton size="xs" variant="ghost" icon={<BsFiles />} aria-label="Duplicate KRA row" onClick={() => duplicateRow(index)} />
                <IconButton size="xs" variant="ghost" colorScheme="red" icon={<BsTrash />} aria-label="Delete KRA row" onClick={() => remove(index)} />
              </HStack>
            </HStack>
            <VStack align="stretch" spacing={3}>
              <Box>
                <Text mb={1} fontSize="xs" color={labelColor} fontWeight="medium">Key Result Area</Text>
                <Input size="sm" placeholder="Key result area" {...register(`sections.${sectionIndex}.items.${index}.title`)} />
              </Box>
              <Box>
                <Text mb={1} fontSize="xs" color={labelColor} fontWeight="medium">KPI / Measure</Text>
                <Input size="sm" placeholder="KPI / Measure" {...register(`sections.${sectionIndex}.items.${index}.kpiMeasure`)} />
              </Box>
              <Box>
                <Text mb={1} fontSize="xs" color={labelColor} fontWeight="medium">Weightage</Text>
                <Input size="sm" type="number" {...register(`sections.${sectionIndex}.items.${index}.weightage`, { valueAsNumber: true })} />
              </Box>
              <SimpleGrid columns={3} spacing={2}>
                <Checkbox size="sm" {...register(`sections.${sectionIndex}.items.${index}.ratingRequired`)}>Required</Checkbox>
                <Checkbox size="sm" {...register(`sections.${sectionIndex}.items.${index}.commentRequired`)}>Comments</Checkbox>
                <Checkbox size="sm" {...register(`sections.${sectionIndex}.items.${index}.evidenceRequired`)}>Evidence</Checkbox>
              </SimpleGrid>
            </VStack>
          </Box>
        ))}
      </VStack>

      <TableContainer display={{ base: "none", md: "block" }} overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead bg={columnHeaderBg}>
            <Tr>
              <Th width="52px">#</Th>
              <Th minW="240px">Key Result Area</Th>
              <Th minW="240px">KPI / Measure</Th>
              <Th isNumeric width="130px">Weightage</Th>
              <Th width="110px">Required</Th>
              <Th width="130px">Comments</Th>
              <Th width="130px">Evidence</Th>
              <Th width="140px">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {fields.map((field, index) => (
              <Tr key={field.fieldKey} borderColor={borderColor}>
                <Td>{index + 1}</Td>
                <Td><Input size="sm" placeholder="Key result area" {...register(`sections.${sectionIndex}.items.${index}.title`)} /></Td>
                <Td><Input size="sm" placeholder="KPI / Measure" {...register(`sections.${sectionIndex}.items.${index}.kpiMeasure`)} /></Td>
                <Td isNumeric><Input size="sm" type="number" textAlign="right" {...register(`sections.${sectionIndex}.items.${index}.weightage`, { valueAsNumber: true })} /></Td>
                <Td><Checkbox {...register(`sections.${sectionIndex}.items.${index}.ratingRequired`)} /></Td>
                <Td><Checkbox {...register(`sections.${sectionIndex}.items.${index}.commentRequired`)} /></Td>
                <Td><Checkbox {...register(`sections.${sectionIndex}.items.${index}.evidenceRequired`)} /></Td>
                <Td>
                  <HStack spacing={1}>
                    <IconButton size="xs" variant="ghost" icon={<BsArrowUp />} aria-label="Move KRA row up" onClick={() => index > 0 && move(index, index - 1)} />
                    <IconButton size="xs" variant="ghost" icon={<BsArrowDown />} aria-label="Move KRA row down" onClick={() => index < fields.length - 1 && move(index, index + 1)} />
                    <IconButton size="xs" variant="ghost" icon={<BsFiles />} aria-label="Duplicate KRA row" onClick={() => duplicateRow(index)} />
                    <IconButton size="xs" variant="ghost" colorScheme="red" icon={<BsTrash />} aria-label="Delete KRA row" onClick={() => remove(index)} />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      <Stack mt={3} direction={{ base: "column", sm: "row" }} justify="space-between" align={{ base: "stretch", sm: "center" }} spacing={2}>
        <Button size="sm" variant="outline" leftIcon={<BsPlus />} onClick={addRow} width={{ base: "100%", sm: "auto" }}>Add KRA row</Button>
        <Text fontSize="sm">
          Configured allocation: {items.reduce((sum, item) => sum + Number(item.weightage || 0), 0)} / {section.weightage || 0}
        </Text>
      </Stack>
    </>
  );
};

export default KraItemsTable;
